/**
 * Вкладка «Биллинг» карточки сделки: все начисления CRM по сделке и её задачам
 * перемещения — кому, сколько, за что, с переключением группировки
 * (`&group=`). Спека `docs/specs/2026-09-25-deal-billing-design.md`; те же
 * данные — `prokat-runner deal-billing ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router";

import { type BillingRow, type BillingTask, type DealBilling, loadDealBilling } from "@/lib/dealBilling";

type Group = "user" | "category" | "task" | "product" | "feed";
const GROUPS: { id: Group; label: string }[] = [
  { id: "user", label: "Сотрудник" },
  { id: "category", label: "Категория" },
  { id: "task", label: "Задача" },
  { id: "product", label: "Товар" },
  { id: "feed", label: "Лента" },
];

const round = (n: number) => Math.round(n * 100) / 100;
const sum = (rows: BillingRow[]) => round(rows.reduce((s, r) => s + r.amount, 0));
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const dt = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");
/** Полное имя и мелко город (`locationShort`): «Гущина Анастасия НН». */
function Who({ name, loc }: { name: string | null; loc: string | null }) {
  return (
    <>
      {name ?? "—"}
      {loc && <span className="ml-1 text-[11px] font-normal text-fg-faint">{loc}</span>}
    </>
  );
}

const signed = (n: number) => (n > 0 ? "text-ok" : n < 0 ? "text-err" : "text-fg-muted");

type Block = { key: string; title: ReactNode; hint?: ReactNode; rows: BillingRow[]; task?: BillingTask };

/** Группы в порядке показа: по убыванию суммы, «Задача» — по времени задач. */
function blocksOf(b: DealBilling, group: Group): Block[] {
  const by = (keyOf: (r: BillingRow) => string, title: (r: BillingRow) => ReactNode, hint?: (rows: BillingRow[]) => ReactNode) => {
    const m = new Map<string, BillingRow[]>();
    for (const r of b.rows) m.set(keyOf(r), [...(m.get(keyOf(r)) ?? []), r]);
    return [...m.entries()]
      .map(([key, rows]) => ({ key, title: title(rows[0]!), hint: hint?.(rows), rows }))
      .sort((x, y) => sum(y.rows) - sum(x.rows));
  };
  if (group === "feed") return [{ key: "feed", title: "Все проводки по времени", rows: b.rows }];
  if (group === "user")
    return by(
      (r) => String(r.user_id),
      (r) => (r.user ? <Who name={r.user} loc={r.user_loc} /> : `#${r.user_id}`),
      (rows) =>
        [...new Set(rows.map((r) => r.category))]
          .map((c) => ({ c, s: sum(rows.filter((r) => r.category === c)) }))
          .sort((x, y) => y.s - x.s)
          .map(({ c, s }) => `${c} ${s}`)
          .join(" · "),
    );
  if (group === "category") return by((r) => r.category, (r) => r.category);
  if (group === "product")
    return by(
      (r) => String(r.serial ?? "—"),
      (r) =>
        r.serial ? (
          <>
            <span className="font-mono">{r.serial}</span> {r.product ?? ""}
          </>
        ) : (
          "Без товара"
        ),
    );
  // «Задача»: все задачи сделки, даже без проводок; потом чужие задачи и «Без задачи».
  const own = new Set(b.tasks.map((t) => t.id));
  const tasks: Block[] = b.tasks.map((t) => ({
    key: `t${t.id}`,
    task: t,
    title: `${t.group ?? t.type ?? "задача"} #${t.id}`,
    rows: b.rows.filter((r) => r.task_id === t.id),
  }));
  const foreign = by(
    (r) => (r.task_id && !own.has(r.task_id) ? `f${r.task_id}` : r.task_id ? "" : "none"),
    (r) => (r.task_id ? `Задача #${r.task_id} (другой сделки)` : "Без задачи"),
  ).filter((x) => x.key);
  return [...tasks, ...foreign];
}

function Stat({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`биллинг «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function TaskHint({ t }: { t: BillingTask }) {
  return (
    <span className="text-fg-muted">
      <span className={t.cancel ? "line-through" : ""}>{t.status ?? "—"}</span> · <Who name={t.user} loc={t.user_loc} /> · {dt(t.done_at ?? t.created)}
      {t.timer_min != null && ` · таймер ${t.timer_min} мин`} · серия <span className="font-mono">{t.serial}</span> {t.product ?? ""}
      {t.comment && <span className="text-fg-faint"> · «{t.comment}»</span>}
      {t.mismatch && (
        <span className="ml-1 inline-flex items-center gap-0.5 text-warn" title="billingSum задачи не равен сумме её проводок">
          <TriangleAlert size={12} aria-hidden /> в задаче {t.billing_sum ?? 0} ₽
        </span>
      )}
    </span>
  );
}

function Rows({ rows, group }: { rows: BillingRow[]; group: Group }) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.id} className="border-t border-border align-top" data-annot={`проводка #${r.id}`}>
          <td className="whitespace-nowrap px-3 py-1 tabular-nums text-fg-faint">{dt(r.at)}</td>
          {group !== "user" && <td className="whitespace-nowrap px-3 py-1">
              <Who name={r.user} loc={r.user_loc} />
            </td>}
          <td className="px-3 py-1">
            {group !== "category" && <span>{r.category} · </span>}
            <span className="text-fg-muted">{r.module}</span>
            {r.correct_of && <span className="text-fg-faint"> (откат #{r.correct_of})</span>}
            {r.comment && <div className="text-[11.5px] text-fg-faint">{r.comment}</div>}
          </td>
          {group !== "task" && <td className="px-3 py-1 tabular-nums text-fg-muted">{r.task_id ? `#${r.task_id}` : ""}</td>}
          {group !== "product" && <td className="px-3 py-1 font-mono text-fg-muted">{r.serial ?? ""}</td>}
          <td className={`px-3 py-1 text-right font-medium tabular-nums ${signed(r.amount)}`}>
            {r.amount > 0 ? "+" : ""}
            {r.amount}
          </td>
        </tr>
      ))}
    </>
  );
}

function Body({ b, group }: { b: DealBilling; group: Group }) {
  const t = b.total;
  const cols = 6 - (["user", "category", "task", "product"].includes(group) ? 1 : 0);
  const blocks = blocksOf(b, group);
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="Итого" value={rub(t.net)} hint="начислено минус списано" />
        <Stat label="Начислено" value={rub(t.accrued)} tone="text-ok" />
        <Stat label="Списано" value={rub(t.written_off)} tone={t.written_off ? "text-err" : ""} hint="откаты и корректировки" />
        <Stat label="Сотрудников" value={t.users} />
        <Stat label="Задач перемещения" value={t.tasks} hint="все по сделке, с начислением и без" />
      </div>
      {b.rows.length || b.tasks.length ? (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border" data-annot="биллинг по группам">
          <table className="w-full text-[12.5px]">
            {blocks.map((x) => (
              <tbody key={x.key}>
                <tr className={`border-t border-border bg-bg-subtle first:border-t-0 ${x.task && !x.rows.length ? "text-fg-muted" : ""}`}>
                  <td colSpan={cols - 1} className="px-3 py-1.5">
                    <span className="font-medium">{x.title}</span>
                    <span className="ml-2 text-[12px] text-fg-faint">{x.rows.length} пров.</span>
                    {(x.task || x.hint) && <div className="text-[12px]">{x.task ? <TaskHint t={x.task} /> : <span className="text-fg-muted">{x.hint}</span>}</div>}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-semibold tabular-nums ${signed(sum(x.rows))}`}>{rub(sum(x.rows))}</td>
                </tr>
                <Rows rows={x.rows} group={group} />
              </tbody>
            ))}
          </table>
        </div>
      ) : (
        <div className="text-[12.5px] text-fg-muted">Начислений и задач перемещения по сделке нет.</div>
      )}
    </>
  );
}

export function DealBillingTab({ dealId }: { dealId: number }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("group");
  const group: Group = GROUPS.some((g) => g.id === raw) ? (raw as Group) : "user";
  const pick = (id: Group) => {
    const out = new URLSearchParams(params);
    if (id === "user") out.delete("group");
    else out.set("group", id);
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["deal-billing", dealId], queryFn: () => loadDealBilling(dealId) });
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]" role="radiogroup" aria-label="Группировка" data-annot="группировка биллинга">
        <span className="text-fg-muted">Группировать:</span>
        {GROUPS.map((g) => (
          <button
            key={g.id}
            type="button"
            role="radio"
            aria-checked={group === g.id}
            onClick={() => pick(g.id)}
            className={`rounded-full border px-2.5 py-0.5 ${group === g.id ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`}
          >
            {g.label}
          </button>
        ))}
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Собираю начисления из CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить биллинг: {String(q.error.message)}</div>
      ) : (
        <Body b={q.data} group={group} />
      )}
    </>
  );
}
